import { Button, Form, Offcanvas } from "react-bootstrap";
import useApi from "../../../utilities/useApi";
import enums from "../../../utilities/enums";
import { useState, useEffect } from "react";
import Loading from "../../common/loading";
import SimpleBar from "simplebar-react";
import SuccessModal from "../../common/successModal";
import arrayUtilities from "../../../utilities/arrayUtilities";
import stringUtilities from "../../../utilities/stringUtilities";
import sketchUtilities from "../../../utilities/sketchUtilities";

export default function DownloadMapPackage(props) {

    const { api } = useApi();
    const [isLoading, setIsLoading] = useState(false);
    const [success, setSuccess] = useState(null);
    const [selectedWorkflowIdsByLimit, setSelectedWorkflowIdsByLimit] = useState({});
    const [includeAllByLimit, setIncludeAllByLimit] = useState({});

    const limits = props.sketch?.limits ?? [];

    let pointsLinesIsDefault = arrayUtilities.isNullOrEmpty(props.workflowPoints) && arrayUtilities.isNullOrEmpty(props.workflowLines) && arrayUtilities.isNullOrEmpty(props.workflowPointsLines);

    let dataWorkflowIds = !arrayUtilities.isNullOrEmpty(props.points) ? arrayUtilities.distinct(props.points.map(x => x.workflowId)) : [];
    let sketchWorkflowIds = !arrayUtilities.isNullOrEmpty(props.sketch?.items) ? arrayUtilities.distinct(props.sketch.items.map(x => x.tool?.properties?.workflowId)) : [];
    let textMarkerWorkflowIds = !arrayUtilities.isNullOrEmpty(props.sketch?.textMarkers) ? arrayUtilities.distinct(props.sketch.textMarkers.map(x => x.workflowId)) : [];

    let combinedWorkflowIds = arrayUtilities.distinct(dataWorkflowIds.concat(sketchWorkflowIds).concat(textMarkerWorkflowIds)).filter(x => !stringUtilities.isNullOrEmpty(x));

    const workflows = (props.workflows ?? []).filter(workflow =>
        combinedWorkflowIds.includes(workflow.id) &&
        (pointsLinesIsDefault || props.workflowPoints.includes(workflow.id) || props.workflowLines.includes(workflow.id) || props.workflowPointsLines.includes(workflow.id))
    );
    const allWorkflowIds = workflows.map(w => w.id);

    useEffect(() => {
        setSelectedWorkflowIdsByLimit({});
        setIncludeAllByLimit({});
    }, [props.sketch?.id]);

    const isWorkflowSelected = (limitIndex, workflowId) => {
        const selected = selectedWorkflowIdsByLimit[limitIndex];
        return selected === undefined ? true : selected.includes(workflowId);
    };

    const toggleWorkflow = (limitIndex, workflowId) => {
        const current = selectedWorkflowIdsByLimit[limitIndex] ?? allWorkflowIds;
        const next = current.includes(workflowId)
            ? current.filter(id => id !== workflowId)
            : [...current, workflowId];
        const updated = { ...selectedWorkflowIdsByLimit };
        if (next.length === allWorkflowIds.length) {
            delete updated[limitIndex];
        } else {
            updated[limitIndex] = next;
        }
        setSelectedWorkflowIdsByLimit(updated);
    };

    const isAllSelected = (limitIndex) => includeAllByLimit[limitIndex] !== false;

    const toggleAll = (limitIndex) => {
        setIncludeAllByLimit(prev => ({ ...prev, [limitIndex]: !isAllSelected(limitIndex) }));
    };

    const toApiSelections = () => {
        return limits.map((limit, limitIndex) => ({
            limitId: limit?.id ?? null,
            limitIndex,
            workflowIds: selectedWorkflowIdsByLimit[limitIndex] ?? allWorkflowIds,
            includeAll: isAllSelected(limitIndex)
        }));
    };

    const getLimitName = (limitIndex) => sketchUtilities.getLimitName(limits, limitIndex);

    const cancel = () => props.onComplete?.();

    const download = () => {
        setIsLoading(true);
        const outputFormat = Number(Object.keys(enums.MapOutputFormat).find(k => enums.MapOutputFormat[k] === props.mapOutputFormat) ?? 0);
        api.sendMapPackage(
            props.organisationId, props.projectId, props.sketchId,
            outputFormat, toApiSelections(), props.basemap, props.includeDeleted, props.selectedSurfaceIds,
            () => {
                setIsLoading(false);
                setSuccess({ message: 'Your map package is being generated and will be emailed to you.' });
            },
            () => setIsLoading(false)
        );
    };

    return <>
        {success && <SuccessModal success={success} onDismiss={() => { setSuccess(null); cancel(); }} />}
        <Offcanvas show placement='end' scroll={false} backdrop={false}>
            <Offcanvas.Header className='border-bottom'>
                <Offcanvas.Title>Download Map Package</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                <div className="d-flex flex-align-start flex-column h-100 w-100 ">
                    <div className="w-100 h-100 mb-auto overflow-y-auto">
                        {isLoading && <Loading />}
                        {!isLoading &&
                            <SimpleBar autoHide={false} className='simplebar-no-autohide pe-3' style={{ maxHeight: 'calc(100vh - 280px)' }}>
                                {limits.map((_, limitIndex) => (
                                    <div key={limitIndex}>
                                        <p className="h6">{getLimitName(limitIndex)}</p>
                                        <div className="mb-3">
                                            <div className='d-flex align-items-center mb-2'>
                                                <Form.Check type='switch' id={`map-pkg-all-${limitIndex}`} checked={isAllSelected(limitIndex)} onChange={() => toggleAll(limitIndex)} className="me-2" />
                                                <p className="mb-0">All</p>
                                            </div>
                                            {workflows.map(wf => (
                                                <div className='d-flex align-items-center mb-2' key={wf.id}>
                                                    <Form.Check type='switch' id={`map-pkg-${limitIndex}-${wf.id}`} checked={isWorkflowSelected(limitIndex, wf.id)} onChange={() => toggleWorkflow(limitIndex, wf.id)} className="me-2" />
                                                    <p className="mb-0">{wf.name || wf.id}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </SimpleBar>
                        }
                    </div>
                    <div className="d-flex justify-content-between order-2 w-100">
                        <Button onClick={cancel} variant="secondary" size="sm">Cancel</Button>
                        <Button size="sm" variant="primary" onClick={download} disabled={isLoading}><i className="fi-download me-2"></i> Download</Button>
                    </div>
                </div>
            </Offcanvas.Body>
        </Offcanvas>
    </>;
}
